import { LitElement, html, css } from 'lit';

/**
 * Composant <ui-rating>
 * Système d'évaluation par étoiles interactif
 */
export class UIRating extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: Number, reflect: true },
    max: { type: Number },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    label: { type: String, reflect: true },
    
    // État de survol temporaire
    hoverValue: { type: Number, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
      user-select: none;
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .rating-container {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .star {
      width: 28px;
      height: 28px;
      cursor: pointer;
      color: var(--ui-border);
      fill: none;
      stroke: currentColor;
      stroke-width: 1.5;
      transition: transform var(--ui-transition-fast), color var(--ui-transition-fast), fill var(--ui-transition-fast);
    }

    :host(:not([disabled])) .star:hover {
      transform: scale(1.2);
    }

    .star.active {
      color: var(--ui-warning);
      fill: var(--ui-warning);
    }

    :host([disabled]) .star {
      cursor: not-allowed;
      opacity: 0.6;
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = 0;
    this.max = 5;
    this.disabled = false;
    this.required = false;
    this.hoverValue = 0;
    this.label = '';
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value ? String(this.value) : '');
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value !== undefined && this.value !== null && this.value > 0;
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez attribuer une note.', this.shadowRoot.querySelector('.rating-container'));
    } else {
      this.internals.setValidity({});
    }
  }

  _handleMouseEnter(val) {
    if (this.disabled) return;
    this.hoverValue = val;
  }

  _handleMouseLeave() {
    if (this.disabled) return;
    this.hoverValue = 0;
  }

  _selectValue(val) {
    if (this.disabled) return;
    
    const oldValue = this.value;
    // Si on clique sur la valeur déjà sélectionnée, on peut la désélectionner (optionnel, ici on remet à 0)
    const newValue = oldValue === val ? 0 : val;
    this.value = newValue;

    if (oldValue !== newValue) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      this.dispatchEvent(new CustomEvent('ui-change', {
        detail: { value: newValue },
        bubbles: true,
        composed: true
      }));
    }
  }

  render() {
    const maxStars = this.max || 5;
    const stars = Array.from({ length: maxStars }, (_, i) => i + 1);
    const displayValue = this.hoverValue > 0 ? this.hoverValue : this.value;

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="rating-container ui-component" part="input" @mouseleave="${this._handleMouseLeave}">
        <!-- Soumission automatique gérée via ElementInternals -->

        ${stars.map(star => {
          const isActive = star <= displayValue;
          return html`
            <svg 
              class="star ${isActive ? 'active' : ''}" 
              viewBox="0 0 24 24" 
              @mouseenter="${() => this._handleMouseEnter(star)}"
              @click="${() => this._selectValue(star)}"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
          `;
        })}
      </div>
    `;
  }
}

if (!customElements.get('ui-rating')) {
  customElements.define('ui-rating', UIRating);
}
